import React, { useRef } from 'react';
import {
  useResponsiveParams,
  QuantaInput,
  QuantaSelect,
  faCancel,
  CircleButton,
  useQuantaFormikContext,
  Text,
  QuantaVoiceActions
} from 'quanta-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

export default function QuickSearchPCV({
  myIntermediaries,
  setAdvancedSearch,
  loading,
  setFieldValue,
  voice = false,
  disabled
}) {
  const { errors, setFieldValue: setFieldValueCxt, resetForm } = useQuantaFormikContext();

  const submitRef = useRef();
  const submitForm = () => { submitRef.current.click() };

  const isMobile = useResponsiveParams({
    for_mobile: true,
    for_desktop: false,
  });

  const voiceArgs = {
    title: 'Search Quotes',
    commands: [
      {
        command: [
          '(search) with customer name *',
          '(search) for customer *',
        ],
        callback: (customerName) => {
          resetForm();
          setFieldValueCxt('customerName', customerName);
          submitForm();
        }
      },
      {
        command: [
          '(search) with (phone) number *',
          '(search) with (mobile) number *'
        ],
        callback: (mobileNumber) => {
          resetForm();
          setFieldValueCxt('customerMobile', mobileNumber.replace(/\s+/g, ''));
          submitForm();
        }
      },
      {
        command: [
          '(search) with (customer) name * and (phone) number *',
          '(search) with (customer) name * and (mobile) number *',
        ],
        callback: (customerName, mobileNumber) => {
          resetForm();
          setFieldValueCxt('customerName', customerName);
          setFieldValueCxt('customerMobile', mobileNumber.replace(/\s+/g, ''));
          submitForm();
        }
      },
      {
        command: [
          '(search) with (phone) number * and (customer) name *',
          '(search) with (mobile) number * and (customer) name *',
        ],
        callback: (mobileNumber, customerName) => {
          resetForm();
          setFieldValueCxt('customerName', customerName);
          setFieldValueCxt('customerMobile', mobileNumber.replace(/\s+/g, ''));
          submitForm();
        }
      },
      {
        command: 'clear',
        callback: ({ resetTranscript }) => {
          resetForm();
          resetTranscript();
        }
      }
    ],
    examples: [
      <span>search with customer name <code>Robert</code></span>,
      <span>search with mobile number <code>9831098310</code></span>,
      <span>search with mobile number <code>9831098310</code> and name <code>Robert</code></span>,
      'clear'
    ]
  }

  return (
    <div className="mt-8 ">
      <div className="space-y-4">
        <div
          className={
            isMobile
              ? `px-2 mx-${isMobile ? 0 : 8
              } border bg-neutral-light shadow rounded-full`
              : 'hidden'
          }
        >
          <QuantaSelect
            noerror={true}
            size="full"
            className={
              'border-0 margin-0 h-10 bg-neutral-light hover:bg-neutral'
            }
            name="intermediaryCode"
            data={
              myIntermediaries
                ? [
                  ...myIntermediaries.map((intermediaryCode) => {
                    return {
                      label: intermediaryCode.intermediaryName
                        ? intermediaryCode.intermediaryName
                        : '',
                      value: intermediaryCode.intermediaryCode
                        ? intermediaryCode.intermediaryCode
                        : '',
                    };
                  }),
                ]
                : []
            }
          />
          <Text severity="error" type="label">
            {errors.intermediaryCode}
          </Text>
        </div>
        <div
          className={`px-2 mx-${isMobile ? 0 : 8
            } border bg-neutral-light shadow rounded-full flex flex-row justify-between`}
        >
          <div className={`pl-4 pr-2 flex flex-row space-x-2 w-full`}>
            <QuantaSelect
              noerror={true}
              className={
                isMobile
                  ? 'hidden'
                  : 'border-0 margin-0 h-8 bg-neutral-light'
              }
              name="intermediaryCode"
              data={
                myIntermediaries
                  ? [
                    ...myIntermediaries.map((intermediaryCode) => {
                      return {
                        label: intermediaryCode.intermediaryName
                          ? intermediaryCode.intermediaryName
                          : '',
                        value: intermediaryCode.intermediaryCode
                          ? intermediaryCode.intermediaryCode
                          : '',
                      };
                    }),
                  ]
                  : []
              }
            />
            <div
              className={
                isMobile
                  ? 'hidden'
                  : 'border-l-2 border-neutral-dark my-2'
              }
            ></div>
            <span className="w-full">
              <QuantaInput
                noerror={true}
                size="full"
                type="text"
                name="fuzzySearch"
                className="border-0 margin-0 h-8 rounded-none bg-neutral-light"
                placeholder="Please enter quote number or customer name or mobile"
                onDataChange={(val) => {
                  if (val.startsWith('Q')) {
                    setFieldValue('quoteNumber', val);
                    setFieldValue('customerMobile', '');
                    setFieldValue('customerName', '');
                  } else if (Number(val)) {
                    setFieldValue('customerMobile', val);
                    setFieldValue('quoteNumber', '');
                    setFieldValue('customerName', '');
                  } else {
                    setFieldValue('customerName', val);
                    setFieldValue('quoteNumber', '');
                    setFieldValue('customerMobile', '');
                  }
                }}
              />
            </span>
          </div>
          <div className="py-2 flex flex-row space-x-2 items-center">
            { voice && <QuantaVoiceActions {...voiceArgs} /> }
            <button disabled={disabled} type="submit" ref={submitRef}>
              <CircleButton>
                {loading ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin={true}
                    size="2x"
                  />
                ) : (
                  <FontAwesomeIcon icon={faSearch} size="2x" />
                )}
              </CircleButton>
            </button>
          </div>
        </div>
      </div>
      <div className="flex justify-between px-10 pt-2">
        {isMobile ? (
          <Text severity="error" type="label">
            {errors.fuzzySearch}
          </Text>
        ) : (
          <div className="-mt-2">
            <Text severity="error" type="label">
              {errors.intermediaryCode}
            </Text>
            <Text severity="error" type="label" className="pl-56">
              {errors.fuzzySearch}
            </Text>
          </div>
        )}
        <span
          className="quanta_link text-sm"
          onClick={(e) => {
            setAdvancedSearch(true);
            setFieldValue('fuzzySearch', '');
          }}
        >
          Advanced Search
        </span>
      </div>
    </div>
  );
}
